import type { ReactNode } from 'react';
import { useState, useEffect, useMemo, useCallback } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import type { Author, Series, Shelf } from '@/api/types';

export type EntityType = 'author' | 'series' | 'shelf';
export type Entity = Author | Series | Shelf;

/** Когда true, при выборе «Создать» не вызываем createFunction, а передаём «ожидающую» сущность (запись в БД при сохранении формы). */
const PENDING_ENTITY_ID = '__pending__';

export interface EntityAutocompleteProps {
  value: Entity | null;
  onChange: (value: Entity | null) => void;
  label: string;
  disabled?: boolean;
  allowCreate?: boolean;
  /** Не создавать сущность в БД при выборе «Создать» — передать в onChange «ожидающую» сущность (id === __pending__). */
  deferCreate?: boolean;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
  searchFunction: (query: string, limit?: number) => Promise<Entity[]>;
  createFunction: (name: string) => Promise<Entity>;
}

const DEBOUNCE_MS = 300;

function makePendingEntity(name: string): Entity {
  return { id: PENDING_ENTITY_ID, name, createdAt: '', updatedAt: '' } as Entity;
}

export function EntityAutocomplete({
  value,
  onChange,
  label,
  disabled = false,
  allowCreate = true,
  deferCreate = false,
  size = 'medium',
  error = false,
  helperText,
  searchFunction,
  createFunction,
}: EntityAutocompleteProps): ReactNode {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Entity[]>([]);
  const [initialOptions, setInitialOptions] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(false);
  const [debouncedInput, setDebouncedInput] = useState('');
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Загрузка начальных данных при первом открытии
  const loadInitialOptions = useCallback(() => {
    setLoading(true);

    // eslint-disable-next-line prefer-const
    let cancelled = false;
    searchFunction('', 10)
      .then((results) => {
        if (!cancelled) {
          setInitialOptions(results);
          setOptions(results);
          setHasInitialLoad(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInitialOptions([]);
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
  }, [searchFunction]);

  // Задержка ввода (debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedInput(inputValue);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  // Поиск при изменении отложенного ввода
  useEffect(() => {
    if (!debouncedInput.trim()) {
      // Если ввод пустой, показываем начальные данные
      if (hasInitialLoad) {
        setOptions(initialOptions);
      } else {
        setOptions([]);
      }
      return;
    }

    let cancelled = false;
    setLoading(true);

    searchFunction(debouncedInput, 10)
      .then((results) => {
        if (!cancelled) {
          setOptions(results);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
    // searchFunction стабильна (из API модулей), но мы включаем её для корректности
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedInput, hasInitialLoad, initialOptions]);

  // Обновление значения ввода при изменении внешнего значения
  useEffect(() => {
    if (value) {
      setInputValue(value.name);
    } else {
      setInputValue('');
    }
  }, [value]);

  // Создание опции "Создать новую..."
  const createOption = useMemo(() => {
    if (!allowCreate || !debouncedInput.trim()) return null;
    const trimmed = debouncedInput.trim();
    const exists = options.some((opt) => opt.name.toLowerCase() === trimmed.toLowerCase());
    if (exists) return null;
    return { id: '__create__', name: trimmed } as Entity;
  }, [allowCreate, debouncedInput, options]);

  const displayOptions = useMemo(() => {
    const opts = [...options];
    if (createOption) {
      opts.push(createOption);
    }
    return opts;
  }, [options, createOption]);

  const handleChange = async (_event: unknown, newValue: Entity | null) => {
    if (!newValue) {
      setInputValue('');
      onChange(null);
      return;
    }

    // Обработка опции создания
    if ((newValue as { id?: string }).id === '__create__') {
      if (deferCreate) {
        const pending = makePendingEntity(newValue.name);
        onChange(pending);
        setInputValue(pending.name);
        return;
      }
      try {
        const created = await createFunction(newValue.name);
        onChange(created);
        setInputValue(created.name);
      } catch (error) {
        console.error('Ошибка создания сущности:', error);
      }
      return;
    }

    onChange(newValue);
    setInputValue(newValue.name);
  };

  return (
    <Autocomplete
      value={value}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onOpen={() => {
        if (!hasInitialLoad && !inputValue.trim() && !loading) {
          loadInitialOptions();
        }
      }}
      options={displayOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option: Entity, val: Entity) => option.id === val.id}
      loading={loading}
      disabled={disabled}
      noOptionsText="Нет вариантов"
      filterOptions={(x) => x} // Отключаем фильтрацию на клиенте, делаем это на сервере
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          fullWidth
          disabled={disabled}
          size={size}
          error={error}
          helperText={helperText}
        />
      )}
      renderOption={(props, option) => {
        const isCreate = (option as { id?: string }).id === '__create__';
        return (
          <li {...props} key={option.id}>
            {isCreate ? `Создать "${option.name}"` : option.name}
          </li>
        );
      }}
    />
  );
}
